import "./ServiceApprovalPage.css";
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabase";

type ApprovalStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "expired"
  | "cancelled";

type ApprovalData = {
  approval_id: string;
  status: ApprovalStatus;
  title: string;
  description: string | null;
  previous_total: number | string;
  additional_amount: number | string;
  proposed_total: number | string;
  requested_at: string;
  expires_at: string | null;
  decided_at: string | null;
  customer_comment: string | null;
  order_number: string;
  organization_name: string;
  customer_name: string | null;
};

function money(value: number | string) {
  return new Intl.NumberFormat("es-PE", {
    style: "currency",
    currency: "PEN",
  }).format(Number(value || 0));
}

function dateTime(value: string | null) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("es-PE", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

const STATUS_LABEL: Record<ApprovalStatus, string> = {
  pending: "Pendiente",
  approved: "Aprobado",
  rejected: "Rechazado",
  expired: "Vencido",
  cancelled: "Cancelado",
};

export function ServiceApprovalPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token")?.trim() ?? "";

  const [approval, setApproval] = useState<ApprovalData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const [deciding, setDeciding] = useState<"approved" | "rejected" | null>(null);

  const load = useCallback(async () => {
    if (!token) {
      setApproval(null);
      setError("El enlace no contiene un token válido.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const { data, error: rpcError } = await supabase.rpc(
      "get_service_approval_by_token",
      { p_token: token },
    );

    if (rpcError) {
      setApproval(null);
      setError("La solicitud no existe o el enlace ya no es válido.");
      setLoading(false);
      return;
    }

    setApproval(data as ApprovalData);
    setLoading(false);
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  async function decide(decision: "approved" | "rejected") {
    if (!approval || approval.status !== "pending") return;

    setDeciding(decision);
    setError(null);

    const { data, error: rpcError } = await supabase.rpc(
      "decide_service_approval",
      {
        p_token: token,
        p_decision: decision,
        p_customer_comment: comment.trim() || null,
      },
    );

    setDeciding(null);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    setApproval(data as ApprovalData);
  }

  if (loading) {
    return (
      <main className="approval-public-page">
        <section className="approval-public-card">
          <span className="eyebrow">Aprobación de servicio</span>
          <h1>Validando solicitud…</h1>
        </section>
      </main>
    );
  }

  if (error && !approval) {
    return (
      <main className="approval-public-page">
        <section className="approval-public-card">
          <span className="eyebrow">Aprobación de servicio</span>
          <h1>Enlace no disponible</h1>
          <div className="form-error">{error}</div>
        </section>
      </main>
    );
  }

  if (!approval) return null;

  const closed = approval.status !== "pending";

  return (
    <main className="approval-public-page">
      <section className="approval-public-card">
        <header className="approval-public-header">
          <div>
            <span className="eyebrow">{approval.organization_name}</span>
            <h1>{approval.title}</h1>
            <p>
              Orden {approval.order_number}
              {approval.customer_name ? ` · ${approval.customer_name}` : ""}
            </p>
          </div>
          <span className={`approval-status approval-status-${approval.status}`}>
            {STATUS_LABEL[approval.status]}
          </span>
        </header>

        {approval.description ? (
          <p className="approval-description">{approval.description}</p>
        ) : null}

        <section className="approval-totals">
          <div>
            <span>Total anterior</span>
            <strong>{money(approval.previous_total)}</strong>
          </div>
          <div>
            <span>Adicional</span>
            <strong>{money(approval.additional_amount)}</strong>
          </div>
          <div className="approval-total-final">
            <span>Nuevo total propuesto</span>
            <strong>{money(approval.proposed_total)}</strong>
          </div>
        </section>

        <dl className="approval-meta">
          <div>
            <dt>Solicitado</dt>
            <dd>{dateTime(approval.requested_at)}</dd>
          </div>
          <div>
            <dt>Vence</dt>
            <dd>{dateTime(approval.expires_at)}</dd>
          </div>
          {approval.decided_at ? (
            <div>
              <dt>Respondido</dt>
              <dd>{dateTime(approval.decided_at)}</dd>
            </div>
          ) : null}
        </dl>

        {error ? <div className="form-error">{error}</div> : null}

        {!closed ? (
          <div className="approval-decision">
            <label>
              Comentario opcional
              <textarea
                rows={4}
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                placeholder="Puedes dejar una observación para el taller."
              />
            </label>

            <div className="approval-buttons">
              <button
                className="approval-reject-button"
                type="button"
                disabled={deciding !== null}
                onClick={() => void decide("rejected")}
              >
                {deciding === "rejected" ? "Registrando…" : "Rechazar"}
              </button>

              <button
                className="approval-approve-button"
                type="button"
                disabled={deciding !== null}
                onClick={() => void decide("approved")}
              >
                {deciding === "approved" ? "Registrando…" : "Aprobar"}
              </button>
            </div>
          </div>
        ) : (
          <div className="approval-closed-message">
            <strong>Respuesta registrada: {STATUS_LABEL[approval.status]}.</strong>
            {approval.customer_comment ? <p>{approval.customer_comment}</p> : null}
          </div>
        )}

        <footer className="approval-footer">
          Esta respuesta queda vinculada a la orden de servicio.
        </footer>
      </section>
    </main>
  );
}
